import { useMutation } from '@tanstack/react-query'

import { ILoadedScript, ITrackedChange, useMst } from '#state'
import { nido } from '#util'
import { EDITOR_SCROLLER_SELECTOR, findOffset } from '#util/scrolling'

import { findChangeMark } from './domHelpers'

export function useBulkReview({
  script,
  onSuccess,
}: {
  script: ILoadedScript
  onSuccess?: Callback
}) {
  const scriptId = script.id

  return useMutation({
    async mutationFn(action: 'approve' | 'reject') {
      const ids = script.changeTrackingData.proposedChanges.map((c) => c.id)

      const isApproval = action === 'approve'
      const promises = ids.map((changeId) => {
        const args = { params: { scriptId, changeId }, body: {} }
        return isApproval
          ? nido.scripts.approveTrackedChange
              .mutate(args)
              .then((resp) =>
                resp.status === 200 ? changeId : Promise.reject(),
              )
              .catch(Promise.reject)
          : nido.scripts.rejectTrackedChange
              .mutate(args)
              .then((resp) =>
                resp.status === 200 ? changeId : Promise.reject(),
              )
              .catch(Promise.reject)
      })

      const eventName = isApproval
        ? 'SCRIPT_TRACKED_CHANGE_BULK_APPROVAL'
        : 'SCRIPT_TRACKED_CHANGE_BULK_REJECTION'
      script.trackEvent(eventName, { changeCount: ids.length })

      return Promise.allSettled(promises)
    },
    onSettled(responses, _error, vars) {
      if (!responses) return

      for (const response of responses) {
        if (response.status === 'fulfilled') {
          const change = { scriptId, changeId: response.value }
          vars === 'approve'
            ? script.changeTrackingData.approveChange(change)
            : script.changeTrackingData.rejectChange(change)
        }
      }
      // i was surprised to find that useMutation.onSuccess fires even if
      // one or more of the promises were rejected
      if (!responses.some((res) => res.status === 'rejected')) {
        onSuccess?.()
      }
    },
  })
}

export function useTrackedChangeThread(script: ILoadedScript) {
  const updateTrackedChangeReply =
    nido.scripts.updateTrackedChangeReply.useMutation({
      onSuccess() {
        script.trackEvent('SCRIPT_TRACKED_CHANGE_REPLY_EDITED')
      },
    })

  const deleteTrackedChangeReply =
    nido.scripts.deleteTrackedChangeReply.useMutation({
      onSuccess() {
        script.trackEvent('SCRIPT_TRACKED_CHANGE_REPLY_DELETED')
      },
    })

  function isSelected(changeId: string) {
    return script.changeTrackingData.selectedChangeId === changeId
  }

  function selectTrackedChange(changeId: string) {
    script.changeTrackingData.setSelectedChangeId(changeId)
  }

  // we intentionally do *not* focus the editor
  function scrollToTrackedChangeMark(changeId: string, isAddition: boolean) {
    const elt = findChangeMark(changeId, isAddition)
    const scroller = document.querySelector(EDITOR_SCROLLER_SELECTOR)
    if (elt && scroller) {
      scroller.scrollTo({
        top: findOffset(scroller, elt),
        behavior: 'smooth',
      })
    }
  }

  function createActionsFor(changeId: string) {
    return {
      onUpdateReply(replyId: string, text: string) {
        updateTrackedChangeReply.mutate({
          params: { scriptId: script.id, changeId, replyId },
          body: { text },
        })
      },
      onDeleteReply(replyId: string) {
        deleteTrackedChangeReply.mutate({
          params: { scriptId: script.id, changeId, replyId },
        })
      },
    }
  }

  return {
    createActionsFor,
    isSelected,
    scrollToTrackedChangeMark,
    selectTrackedChange,
  }
}

export function useTrackedChangeStatus(
  script: ILoadedScript,
  change: ITrackedChange,
) {
  const mst = useMst()

  const isProposed = !change.reviewer
  const canReview = mst.user.canReviewTrackedChanges && !change.isOrphaned
  const isSameUser = mst.user.id === change.creator.id
  const canModify = !script.readOnlyByPlan

  const approveMutation = nido.scripts.approveTrackedChange.useMutation({
    onSuccess(_, vars) {
      script.changeTrackingData.approveChange(vars.params)
      script.trackEvent('SCRIPT_TRACKED_CHANGE_APPROVED')
    },
  })

  const rejectMutation = nido.scripts.rejectTrackedChange.useMutation({
    onSuccess(_, vars) {
      script.changeTrackingData.rejectChange(vars.params)
      script.trackEvent('SCRIPT_TRACKED_CHANGE_REJECTED')
    },
  })

  const deleteMutation = nido.scripts.deleteTrackedChange.useMutation({
    onSuccess(_, vars) {
      const isAddition = change.changeType === 'addition'
      script.changeTrackingData.deleteChange({ isAddition, ...vars.params })
      script.trackEvent('SCRIPT_TRACKED_CHANGE_DELETED')
    },
  })

  const unresolveMutation = nido.scripts.unreviewTrackedChange.useMutation({
    onSuccess(_, vars) {
      script.changeTrackingData.unreviewChange(vars.params)
      script.trackEvent('SCRIPT_TRACKED_CHANGE_UNRESOLVED')
    },
  })

  const isApproving = approveMutation.isPending
  const isRejecting = rejectMutation.isPending
  const isDeleting = deleteMutation.isPending
  const isUnresolving = unresolveMutation.isPending

  const commonArgs = {
    params: { scriptId: script.id, changeId: change.id },
    body: {},
  }

  function resetError() {
    approveMutation.reset()
    rejectMutation.reset()
    deleteMutation.reset()
    unresolveMutation.reset()
  }

  return {
    isPending: isApproving || isRejecting || isDeleting || isUnresolving,
    hasError:
      approveMutation.isError ||
      rejectMutation.isError ||
      deleteMutation.isError ||
      unresolveMutation.isError,
    resetError,
    canApprove: isProposed && canReview && canModify,
    canReject: isProposed && canReview && canModify,
    canDelete: isProposed && isSameUser && canModify,
    // resolved changes persist after change-tracking has been turned off
    canUnresolve:
      !isProposed && canReview && canModify && script.trackingChanges,
    approve() {
      approveMutation.mutate(commonArgs)
    },
    reject() {
      rejectMutation.mutate(commonArgs)
    },
    delete() {
      deleteMutation.mutate(commonArgs)
    },
    unresolve() {
      unresolveMutation.mutate(commonArgs)
    },
  }
}
